import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getVendorTariffs, createVendorTariff, deleteVendorTariff, getVendorDetails, getVendorGlCodeDropdown, getVendorBillingSettings } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import * as routes from '../../../Routing/routes';

const VendorTariff = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        vendorTariffsData,
        vendorTariffsLoading,
        createVendorTariffLoading,
        deleteVendorTariffLoading,
        vendorDetailsData,
        vendorGlCodeDropdownData,
        vendorGlCodeDropdownLoading,
        vendorBillingSettingsData,
    } = useSelector(state => state.VendorReducers);

    // Vendor Settings > Billing > Item Type decides how a tariff is charged:
    // UNIT bills quantity x rate, AMOUNT bills a fixed sum. The form always said
    // "Unit Rate", so an amount-wise society saw the wrong wording everywhere.
    const isAmountWise = String(vendorBillingSettingsData?.head_type || '').toUpperCase() === 'AMOUNT';
    const rateLabel = isAmountWise ? 'Amount' : 'Unit Rate';

    const [showAddForm, setShowAddForm] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [tariffToDelete, setTariffToDelete] = useState(null);
    const [formData, setFormData] = useState({
        itemName: '',
        itemType: '',
        glName: '',
        unitRate: '',
        cgstRate: '',
        sgstRate: '',
        igstRate: '',
        tdsRate: '',
        tdsSection: '',
    });

    const tdsSections = [
        { value: '194C', label: '194C - Payment to Contractors' },
        { value: '194J', label: '194J - Professional/Technical Fees' },
        { value: '194H', label: '194H - Commission/Brokerage' },
        { value: '194I', label: '194I - Rent' },
        { value: '194A', label: '194A - Interest (Other than Securities)' },
        { value: '194Q', label: '194Q - Purchase of Goods' },
        { value: '194M', label: '194M - Payment to Commission Agent' },
    ];
    const [formErrors, setFormErrors] = useState({});

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getVendorTariffs({ vendorId: id }));
        dispatch(getVendorDetails({ vendorId: id }));
        dispatch(getVendorGlCodeDropdown({ societyId, type: 'account', groupGlCode: '04' }));
        dispatch(getVendorBillingSettings({ societyId }));
    }, [dispatch, id, societyId, refreshKey]);

    const glCodeOptions = useMemo(() => {
        if (!Array.isArray(vendorGlCodeDropdownData)) return [];
        return vendorGlCodeDropdownData.map(gl => ({
            value: gl.ac_code ,
            label: gl.name ,
        }));
    }, [vendorGlCodeDropdownData]);

    const tariffs = useMemo(() => {
        if (!Array.isArray(vendorTariffsData)) return [];
        return vendorTariffsData.map(tariff => ({
            // Model PK is `vendor_tariff_id`. Older code paths used `tariff_id`
            // or a generic `id` — kept as fallbacks so we don't silently break
            // if the API shape ever regresses.
            id: tariff.vendor_tariff_id || tariff.tariff_id || tariff.id,
            itemName: tariff.item_name,
            itemType: tariff.item_type || '',
            // Empty when unset, not a made-up "GL-<id>": that placeholder saved a
            // GL account that does not exist in the chart, and a bill posted
            // against it produced no journal entry. The picker is required, so
            // an empty value makes the user choose a real account.
            glName: tariff.gl_name || '',
            unitRate: parseFloat(tariff.unit_rate) || 0,
            cgstRate: parseFloat(tariff.cgst_rate) || 0,
            sgstRate: parseFloat(tariff.sgst_rate) || 0,
            igstRate: parseFloat(tariff.igst_rate) || 0,
            tdsRate: parseFloat(tariff.tds_rate) || 0,
            tdsSection: tariff.tds_section || '',
        }));
    }, [vendorTariffsData]);

    const vendorName = vendorDetailsData?.business_name || '';

    const validateForm = () => {
        const errors = {};
        if (!formData.itemName.trim()) errors.itemName = 'Item name is required';
        if (!formData.itemType) errors.itemType = 'Item type is required';
        if (!formData.glName.trim()) errors.glName = 'GL name is required';
        if (!formData.unitRate || parseFloat(formData.unitRate) <= 0) {
            errors.unitRate = `Valid ${isAmountWise ? 'amount' : 'unit rate'} is required`;
        }
        if (formData.cgstRate !== '' && parseFloat(formData.cgstRate) < 0) errors.cgstRate = 'CGST rate cannot be negative';
        if (formData.sgstRate !== '' && parseFloat(formData.sgstRate) < 0) errors.sgstRate = 'SGST rate cannot be negative';
        if (formData.igstRate !== '' && parseFloat(formData.igstRate) < 0) errors.igstRate = 'Valid IGST rate is required';
        if (formData.tdsRate !== '' && parseFloat(formData.tdsRate) < 0) errors.tdsRate = 'Valid TDS rate is required';
        if (formData.tdsRate && !formData.tdsSection) errors.tdsSection = 'TDS section is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (field, value) => { 
        setFormData(prev => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleAddTariff = () => {
        if (!validateForm()) return;

        dispatch(createVendorTariff({
            vendorId: id,
            societyId,
            itemName: formData.itemName,
            itemType: formData.itemType,
            glName: formData.glName,
            unitRate: parseFloat(formData.unitRate),
            cgstRate: parseFloat(formData.cgstRate) || 0,
            sgstRate: parseFloat(formData.sgstRate) || 0,
            igstRate: parseFloat(formData.igstRate) || 0,
            tdsRate: parseFloat(formData.tdsRate) || 0,
            tdsSection: formData.tdsSection || null,
            onSuccess: () => {
                setFormData({ itemName: '', itemType: '', glName: '', unitRate: '', cgstRate: '', sgstRate: '', igstRate: '', tdsRate: '', tdsSection: '' });
                setShowAddForm(false);
                dispatch(getVendorTariffs({ vendorId: id }));
            }
        }));
    };

    const handleDeleteClick = (tariff) => {
        setTariffToDelete(tariff);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (tariffToDelete) {
            dispatch(deleteVendorTariff({
                vendorId: id,
                tariffId: tariffToDelete.id,
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    setTariffToDelete(null);
                    dispatch(getVendorTariffs({ vendorId: id }));
                }
            }));
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModalOpen(false);
        setTariffToDelete(null);
    };

    const handleCancelAdd = () => {
        setShowAddForm(false);
        setFormData({ itemName: '', itemType: '', glName: '', unitRate: '', cgstRate: '', sgstRate: '', igstRate: '', tdsRate: '', tdsSection: '' });
        setFormErrors({});
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        onClick={() => navigate(routes.SOCIETY_PORTAL + routes.SOCIETY_PORTAL_VENDORS)}
                        className="tw-p-2 tw-rounded-lg tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-600"
                    >
                        <Icon icon="mdi:arrow-left" className="tw-text-lg" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Vendor Tariffs</h1>
                        <p className="tw-text-xs tw-text-gray-500">{vendorName ? `Tariffs for ${vendorName}` : 'Manage vendor tariff rates'}</p>
                    </div>
                </div>
                {!showAddForm && (
                    <button
                        onClick={() => setShowAddForm(true)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:plus" /> Add Tariff
                    </button>
                )}
            </div>

            {/* Add Tariff Form */}
            {showAddForm && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-mb-4">
                    <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-100 tw-bg-blue-50">
                        <h3 className="tw-text-sm tw-font-semibold tw-text-blue-800">Add New Tariff</h3>
                    </div>
                    <div className="tw-p-4">
                        <div className="tw-grid tw-grid-cols-3 md:tw-grid-cols-5 tw-gap-3">
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1 tw-block">
                                    Item Name <span className="tw-text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.itemName}
                                    onChange={(e) => handleInputChange('itemName', e.target.value)}
                                    placeholder="Enter item name"
                                    className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${formErrors.itemName ? 'tw-border-red-300' : 'tw-border-gray-200'}`}
                                />
                                {formErrors.itemName && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{formErrors.itemName}</p>}
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1 tw-block">
                                    Item Type <span className="tw-text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.itemType}
                                    onChange={(e) => handleInputChange('itemType', e.target.value)}
                                    className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${formErrors.itemType ? 'tw-border-red-300' : 'tw-border-gray-200'}`}
                                >
                                    <option value="">Select Type</option>
                                    <option value="Service">Service</option>
                                    <option value="Goods">Goods</option>
                                </select>
                                {formErrors.itemType && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{formErrors.itemType}</p>}
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1 tw-block">
                                    GL Name <span className="tw-text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.glName}
                                    onChange={(e) => handleInputChange('glName', e.target.value)}
                                    disabled={vendorGlCodeDropdownLoading}
                                    className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed ${formErrors.glName ? 'tw-border-red-300' : 'tw-border-gray-200'}`}
                                >
                                    <option value="">{vendorGlCodeDropdownLoading ? 'Loading...' : 'Select GL Name'}</option>
                                    {glCodeOptions.map(gl => (
                                        <option key={gl.value} value={gl.value}>{gl.label}</option>
                                    ))}
                                </select>
                                {formErrors.glName && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{formErrors.glName}</p>}
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1 tw-block">
                                    {rateLabel} <span className="tw-text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    value={formData.unitRate}
                                    onChange={(e) => handleInputChange('unitRate', e.target.value)}
                                    placeholder="0.00"
                                    className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${formErrors.unitRate ? 'tw-border-red-300' : 'tw-border-gray-200'}`}
                                />
                                {formErrors.unitRate && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{formErrors.unitRate}</p>}
                                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">
                                    {isAmountWise ? 'Billed as a fixed amount (Vendor Settings: Amount Wise).' : 'Billed as quantity x rate (Vendor Settings: Unit Wise).'}
                                </p>
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1 tw-block">
                                    CGST Rate (%)
                                </label>
                                <input
                                    type="number"
                                    value={formData.cgstRate}
                                    onChange={(e) => handleInputChange('cgstRate', e.target.value)}
                                    placeholder="0"
                                    className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${formErrors.cgstRate ? 'tw-border-red-300' : 'tw-border-gray-200'}`}
                                />
                                {formErrors.cgstRate && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{formErrors.cgstRate}</p>}
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1 tw-block">
                                    SGST Rate (%)
                                </label>
                                <input
                                    type="number"
                                    value={formData.sgstRate}
                                    onChange={(e) => handleInputChange('sgstRate', e.target.value)}
                                    placeholder="0"
                                    className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${formErrors.sgstRate ? 'tw-border-red-300' : 'tw-border-gray-200'}`}
                                />
                                {formErrors.sgstRate && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{formErrors.sgstRate}</p>}
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1 tw-block">
                                    IGST Rate (%)
                                </label>
                                <input
                                    type="number"
                                    value={formData.igstRate}
                                    onChange={(e) => handleInputChange('igstRate', e.target.value)}
                                    placeholder="0"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${formErrors.igstRate ? 'tw-border-red-300' : 'tw-border-gray-200'}`}
                                />
                                {formErrors.igstRate && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{formErrors.igstRate}</p>}
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1 tw-block">
                                    TDS Rate (%)
                                </label>
                                <input
                                    type="number"
                                    value={formData.tdsRate}
                                    onChange={(e) => handleInputChange('tdsRate', e.target.value)}
                                    placeholder="0"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${formErrors.tdsRate ? 'tw-border-red-300' : 'tw-border-gray-200'}`}
                                />
                                {formErrors.tdsRate && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{formErrors.tdsRate}</p>}
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1 tw-block">
                                    TDS Section
                                </label>
                                <select
                                    value={formData.tdsSection}
                                    onChange={(e) => handleInputChange('tdsSection', e.target.value)}
                                    className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${formErrors.tdsSection ? 'tw-border-red-300' : 'tw-border-gray-200'}`}
                                >
                                    <option value="">Select Section</option>
                                    {tdsSections.map(s => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                                {formErrors.tdsSection && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{formErrors.tdsSection}</p>}
                            </div>
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-4">
                            <button
                                onClick={handleCancelAdd}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddTariff}
                                disabled={createVendorTariffLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-bg-blue-400 disabled:tw-cursor-not-allowed"
                            >
                                {createVendorTariffLoading && <Icon icon="mdi:loading" className="tw-animate-spin" />}
                                Add Tariff
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {vendorTariffsLoading && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                    <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading tariffs...</p>
                </div>
            )}

            {/* Tariff Table */}
            {!vendorTariffsLoading && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                    <table className="tw-w-full tw-text-sm">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">#</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Item Name</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">{rateLabel}</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">CGST Rate</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">SGST Rate</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">IGST Rate</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">TDS Rate</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">TDS Section</th>
                                <th className="tw-text-center tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tariffs.map((tariff, index) => (
                                <tr key={tariff.id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50">
                                    <td className="tw-px-4 tw-py-3 tw-text-gray-500">{index + 1}</td>
                                    <td className="tw-px-4 tw-py-3 tw-font-medium tw-text-gray-800">{tariff.itemName}</td>
                                    <td className="tw-px-4 tw-py-3 tw-text-right tw-text-gray-800">{tariff.unitRate.toFixed(2)}</td>
                                    <td className="tw-px-4 tw-py-3 tw-text-right tw-text-gray-800">{tariff.cgstRate}%</td>
                                    <td className="tw-px-4 tw-py-3 tw-text-right tw-text-gray-800">{tariff.sgstRate}%</td>
                                    <td className="tw-px-4 tw-py-3 tw-text-right tw-text-gray-800">{tariff.igstRate}%</td>
                                    <td className="tw-px-4 tw-py-3 tw-text-right tw-text-gray-800">{tariff.tdsRate}%</td>
                                    <td className="tw-px-4 tw-py-3 tw-text-gray-600">{tariff.tdsSection || '-'}</td>
                                    <td className="tw-px-4 tw-py-3 tw-text-center">
                                        <button
                                            onClick={() => handleDeleteClick(tariff)}
                                            className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500"
                                            title="Delete"
                                        >
                                            <Icon icon="mdi:delete-outline" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {tariffs.length === 0 && (
                        <div className="tw-text-center tw-py-12">
                            <Icon icon="mdi:file-document-outline" className="tw-text-4xl tw-text-gray-300" />
                            <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No tariffs found</p>
                            <p className="tw-text-gray-400 tw-text-xs tw-mt-1">Click "Add Tariff" to add a new tariff rate</p>
                        </div>
                    )}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:delete-alert" className="tw-text-red-600 tw-text-xl" />
                            </div>
                            <div>
                                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">Delete Tariff</h3>
                                <p className="tw-text-sm tw-text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-6">
                            Are you sure you want to delete tariff <span className="tw-font-semibold">{tariffToDelete?.itemName}</span>?
                        </p>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                            <button
                                onClick={handleDeleteCancel}
                                disabled={deleteVendorTariffLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleteVendorTariffLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50"
                            >
                                {deleteVendorTariffLoading ? (
                                    <>
                                        <Icon icon="mdi:loading" className="tw-animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="mdi:delete" />
                                        Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VendorTariff;
